import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useFormContext } from '@context/FormContext'
import { AnsweredStrip, QuestionCard } from './EligibilityShared'

/* ── Options ── */
const options = {
  boolean: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ],
  reasonHere: [
    { value: 'heat', label: 'Heating Equipment', sub: 'Furnace, Boiler', emoji: '🔆' },
    { value: 'dms', label: 'Heat Pump System', sub: 'Ductless Mini Split, Central Heat Pump', emoji: '🌡️' },
  ],
  rentOrOwn: [
    { value: 'rent', label: 'I rent', emoji: '📄' },
    { value: 'own', label: 'I own', emoji: '🔑' },
  ],
  homeAge: [
    { value: 'new', label: 'New Construction' },
    { value: '1-10', label: '1–10 Years' },
    { value: '11-20', label: '11–20 Years' },
    { value: '20+', label: '20+ Year' },
  ],
  familySize: [
    { value: '1', label: 'Single-family' },
    { value: '1-4', label: '1–4 Multi-Family' },
    { value: '5+', label: '5+ Units' },
  ],
  howHeated: [
    { value: 'Gas', label: 'Natural Gas', icon: '🔥', desc: "Connected to gas lines." },
    { value: 'Oil', label: 'Heating Oil', icon: '🛢', desc: 'Truck delivery to your home.' },
    { value: 'Propane', label: 'Propane', icon: '⛽', desc: 'Tank near your system.' },
    { value: 'Electric', label: 'Electric', icon: '⚡', desc: "Connected to your electrical system." },
  ],
  maybeBool: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'maybe', label: 'Unsure' },
  ],
}

const HvacSales = ({ currentStepIndex, setValidationSchema, setBlockNextStepUntilHowHeated }) => {
  const { values, setFieldValue, validateForm } = useFormContext()
  if (!values) return null

  const eligibilitySchema = useMemo(
    () =>
      yup.object().shape({
        fuelAssistance: yup
          .string()
          .oneOf(['yes', 'no'])
          .when(['path', 'salesOrService'], {
            is: (path, salesOrService) => path === 'hvac' || salesOrService === 'sales',
            then: yup.string().required('This field is required.'),
          }),
        reasonHere: yup
          .string()
          .oneOf(options.reasonHere.map(({ value }) => value))
          .when(['path', 'salesOrService', 'fuelAssistance'], {
            is: (path, salesOrService, fuelAssistance) =>
              (path === 'hvac' || salesOrService === 'sales') && fuelAssistance === 'no',
            then: yup.string().required('This field is required.'),
          }),
        existingDuctWork: yup
          .string()
          .oneOf(options.maybeBool.map(({ value }) => value))
          .when(['reasonHere', 'fuelAssistance'], {
            is: (reasonHere, fuelAssistance) => reasonHere === 'ac' && fuelAssistance === 'no',
            then: yup.string().required('This field is required'),
          }),
        rentOrOwn: yup
          .string()
          .oneOf(options.rentOrOwn.map(({ value }) => value))
          .when(
            ['path', 'salesOrService', 'existingDuctWork', 'reasonHere', 'fuelAssistance'],
            {
              is: (path, salesOrService, existingDuctWork, reasonHere, fuelAssistance) =>
                (path === 'hvac' || salesOrService === 'sales') &&
                fuelAssistance === 'no' &&
                (existingDuctWork || reasonHere !== 'ac'),
              then: yup.string().required('This field is required.'),
            }
          ),
        homeAge: yup
          .string()
          .oneOf(options.homeAge.map(({ value }) => value))
          .when(['path', 'salesOrService', 'rentOrOwn', 'fuelAssistance'], {
            is: (path, salesOrService, rentOrOwn, fuelAssistance) =>
              (path === 'hvac' || salesOrService === 'sales') &&
              rentOrOwn === 'own' &&
              fuelAssistance === 'no',
            then: yup.string().required('This field is required.'),
          }),
        familySize: yup
          .string()
          .oneOf(options.familySize.map(({ value }) => value))
          .when(['homeAge', 'rentOrOwn', 'fuelAssistance'], {
            is: (homeAge, rentOrOwn, fuelAssistance) =>
              fuelAssistance === 'no' &&
              homeAge != null &&
              ['1-10', '11-20', '20+'].includes(String(homeAge)),
            then: yup.string().required('This field is required.'),
          }),
        howHeated: yup
          .string()
          .oneOf(options.howHeated.map(({ value }) => value))
          .when(['familySize', 'fuelAssistance'], {
            is: (familySize, fuelAssistance) =>
              fuelAssistance === 'no' &&
              familySize != null &&
              ['1', '1-4'].includes(String(familySize)),
            then: yup.string().required('This field is required.'),
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStepIndex]
  )

  useEffect(() => {
    setValidationSchema(eligibilitySchema)
  }, [eligibilitySchema, setValidationSchema])

  useEffect(() => {
    validateForm(eligibilitySchema)
  }, [values, eligibilitySchema, validateForm])

  useEffect(() => {
    if (typeof setBlockNextStepUntilHowHeated !== 'function') return
    const howHeatedVisible =
      values.fuelAssistance === 'no' &&
      values.familySize != null &&
      values.familySize !== '' &&
      ['1', '1-4'].includes(String(values.familySize))
    const howHeatedEmpty =
      values.howHeated == null ||
      values.howHeated === '' ||
      (typeof values.howHeated === 'string' && values.howHeated.trim() === '')
    setBlockNextStepUntilHowHeated(howHeatedVisible && howHeatedEmpty)
  }, [values.fuelAssistance, values.familySize, values.howHeated, setBlockNextStepUntilHowHeated])

  const setAnswer = (field, value) => setFieldValue(field, value)
  const editField = (field) => setFieldValue(field, undefined)

  /* ── Derived visibility ── */
  const fuelAssistanceAnswered = values.fuelAssistance != null && values.fuelAssistance !== ''
  const reasonHereVisible = fuelAssistanceAnswered && values.fuelAssistance === 'no'
  const reasonHereAnswered = reasonHereVisible && values.reasonHere != null && values.reasonHere !== ''

  // existingDuctWork - only for 'ac' reasonHere (not in new mockup but keep logic)
  // Skipping ac path since it's not in the new HEA/HVAC simplified mockup flow

  const rentOrOwnVisible =
    fuelAssistanceAnswered &&
    values.fuelAssistance === 'no' &&
    reasonHereAnswered &&
    (values.reasonHere !== 'ac' || values.existingDuctWork)
  const rentOrOwnAnswered = rentOrOwnVisible && values.rentOrOwn != null && values.rentOrOwn !== ''

  const homeAgeVisible = rentOrOwnAnswered && values.rentOrOwn === 'own' && values.fuelAssistance === 'no'
  const homeAgeAnswered = homeAgeVisible && values.homeAge != null && values.homeAge !== ''

  const familySizeVisible =
    fuelAssistanceAnswered &&
    values.fuelAssistance === 'no' &&
    homeAgeAnswered &&
    ['1-10', '11-20', '20+'].includes(String(values.homeAge))
  const familySizeAnswered = familySizeVisible && values.familySize != null && values.familySize !== ''

  const howHeatedVisible =
    fuelAssistanceAnswered &&
    values.fuelAssistance === 'no' &&
    familySizeAnswered &&
    ['1', '1-4'].includes(String(values.familySize))
  const howHeatedAnswered = howHeatedVisible && values.howHeated != null && values.howHeated !== ''

  /* ── Dot count ── */
  const dotCount = 5
  let activeDot = 0
  if (!fuelAssistanceAnswered) activeDot = 0
  else if (!reasonHereAnswered && reasonHereVisible) activeDot = 1
  else if (!rentOrOwnAnswered && rentOrOwnVisible) activeDot = 2
  else if (!homeAgeAnswered && homeAgeVisible) activeDot = 3
  else activeDot = 4

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-6" style={{ animation: 'fadeUp 0.4s ease both' }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#4675ad' }}>
          HVAC Consultation
        </p>
        <h1
          className="text-3xl text-stone-900 leading-tight mb-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 }}
        >
          Tell us about your home
        </h1>
        <p className="text-stone-500 text-sm">A few quick questions to match you with the right equipment and rebates.</p>
      </div>

      {/* Dot progress */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {Array.from({ length: dotCount }).map((_, i) => (
          <div
            key={i}
            className={`q-dot${i === activeDot ? ' active' : i < activeDot ? ' done' : ''}`}
          />
        ))}
      </div>

      {/* Q1: fuelAssistance */}
      {fuelAssistanceAnswered ? (
        <AnsweredStrip
          questionLabel="Fuel assistance / discount rate?"
          answerLabel={values.fuelAssistance === 'yes' ? 'Yes' : 'No'}
          answerType={values.fuelAssistance === 'yes' ? 'yes' : 'no'}
          onEdit={() => editField('fuelAssistance')}
        />
      ) : (
        <QuestionCard meta="Question 1 of 5">
          <div className="q-text">
            Are you on a utility discount rate or do you receive fuel assistance?
          </div>
          <div className="q-hint">Examples: R2 or R4 on your utility bill, LIHEAP, or similar income-based program.</div>
          <div className="yn-grid">
            <button
              type="button"
              className="yn-btn"
              onClick={() => setAnswer('fuelAssistance', 'yes')}
            >
              <div className="yn-icon">✓</div>
              <div className="yn-label">Yes</div>
            </button>
            <button
              type="button"
              className="yn-btn"
              onClick={() => setAnswer('fuelAssistance', 'no')}
            >
              <div className="yn-icon">✗</div>
              <div className="yn-label">No</div>
            </button>
          </div>
          {/* Notice if yes (shown after answer to help UX) */}
        </QuestionCard>
      )}

      {/* Notice if fuelAssistance is yes */}
      {values.fuelAssistance === 'yes' && (
        <div className="inelig-notice mt-2">
          <strong>Note:</strong> Based on your utility discount rate, we recommend starting with a Mass Save Home Energy Assessment to help you qualify for additional savings and rebates. Please go back and select the Home Energy Assessment option.
        </div>
      )}

      {/* Q2: reasonHere (equipment type) */}
      {reasonHereVisible && (
        reasonHereAnswered ? (
          <AnsweredStrip
            questionLabel="Equipment type"
            answerLabel={options.reasonHere.find((o) => o.value === values.reasonHere)?.label || values.reasonHere}
            answerType="other"
            onEdit={() => editField('reasonHere')}
          />
        ) : (
          <QuestionCard meta="Question 2 of 5">
            <div className="q-text">What kind of equipment are you looking for?</div>
            <div className="mc-grid cols-2" style={{ marginTop: 14 }}>
              {options.reasonHere.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`mc-btn${values.reasonHere === opt.value ? ' selected' : ''}`}
                  onClick={() => setAnswer('reasonHere', opt.value)}
                >
                  <div className="mc-emoji">{opt.emoji}</div>
                  <div className="mc-label">{opt.label}</div>
                  <div className="mc-sub">{opt.sub}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q3: rentOrOwn */}
      {rentOrOwnVisible && (
        rentOrOwnAnswered ? (
          <AnsweredStrip
            questionLabel="Rent or own?"
            answerLabel={values.rentOrOwn === 'own' ? 'I own it' : 'I rent'}
            answerType="other"
            onEdit={() => editField('rentOrOwn')}
          />
        ) : (
          <QuestionCard meta="Question 3 of 5">
            <div className="q-text">Do you rent or own your home?</div>
            <div className="yn-grid">
              {options.rentOrOwn.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`yn-btn${values.rentOrOwn === opt.value ? ' sel-yes' : ''}`}
                  onClick={() => setAnswer('rentOrOwn', opt.value)}
                >
                  <div className="yn-icon">{opt.emoji}</div>
                  <div className="yn-label">{opt.label}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q4: homeAge (only if 'own') */}
      {homeAgeVisible && (
        homeAgeAnswered ? (
          <AnsweredStrip
            questionLabel="Home age"
            answerLabel={options.homeAge.find((o) => o.value === values.homeAge)?.label || values.homeAge}
            answerType="other"
            onEdit={() => editField('homeAge')}
          />
        ) : (
          <QuestionCard meta="Question 4 of 5">
            <div className="q-text">What is the age of your home?</div>
            <div className="mc-grid cols-2" style={{ marginTop: 14 }}>
              {options.homeAge.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`mc-btn${values.homeAge === opt.value ? ' selected' : ''}`}
                  onClick={() => setAnswer('homeAge', opt.value)}
                >
                  <div className="mc-label">{opt.label}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q5: familySize */}
      {familySizeVisible && (
        familySizeAnswered ? (
          <AnsweredStrip
            questionLabel="Home type"
            answerLabel={options.familySize.find((o) => o.value === values.familySize)?.label || values.familySize}
            answerType="other"
            onEdit={() => editField('familySize')}
          />
        ) : (
          <QuestionCard meta="Question 5 of 5">
            <div className="q-text">Is your home a single-family residence, or does it have multiple units?</div>
            <div className="mc-grid cols-2" style={{ marginTop: 14 }}>
              {options.familySize.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`mc-btn${values.familySize === opt.value ? ' selected' : ''}`}
                  onClick={() => setAnswer('familySize', opt.value)}
                >
                  <div className="mc-label">{opt.label}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}

      {/* Q6: howHeated (when familySize is 1 or 1-4) */}
      {howHeatedVisible && (
        howHeatedAnswered ? (
          <AnsweredStrip
            questionLabel="Current heating fuel"
            answerLabel={options.howHeated.find((o) => o.value === values.howHeated)?.label || values.howHeated}
            answerType="other"
            onEdit={() => editField('howHeated')}
          />
        ) : (
          <QuestionCard meta="Heating Fuel">
            <div className="q-text">How is your home currently heated?</div>
            <div className="fuel-grid">
              {options.howHeated.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`fuel-card${values.howHeated === opt.value ? ' selected' : ''}`}
                  onClick={() => setAnswer('howHeated', opt.value)}
                >
                  <div className="fuel-icon">{opt.icon}</div>
                  <div className="fuel-label">{opt.label}</div>
                  <div className="fuel-desc">{opt.desc}</div>
                </button>
              ))}
            </div>
          </QuestionCard>
        )
      )}
    </div>
  )
}

HvacSales.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setValidationSchema: PropTypes.func.isRequired,
  setBlockNextStepUntilHowHeated: PropTypes.func,
  values: PropTypes.object,
}

export default HvacSales
